import React, { useState, useEffect, useRef, useCallback } from 'react';
import { INITIAL_COINS_PER_ROUND, PRACTICE_ROUND_DURATION_SECONDS } from '../../gameConfig';
import '../Game/GamePlay/GamePlay.css';
import './TutorialResults.css';
import coin from '../../assets/coin.svg';
import randomOpponentAvatar from '../../assets/random.png';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();
const TUTORIAL_ROUND_DURATION = PRACTICE_ROUND_DURATION_SECONDS;
const POPUP_DURATION = 30; // 30 seconds for results popup

interface TutorialRoundProps {
  prolificId: string;
  onComplete: () => void;
}

interface TutorialResults {
  playerInvestment: number;
  partnerInvestment: number;
  playerKept: number;
  partnerKept: number;
  poolTotal: number;
  poolMultiplied: number;
  poolShare: number;
  playerFluctuation: number;
  partnerFluctuation: number;
  playerEarnings: number;
  partnerEarnings: number;
  playerNetGain: number;
  partnerNetGain: number;
}

const TutorialRound: React.FC<TutorialRoundProps> = ({ prolificId, onComplete }) => {
  const [sliderValue, setSliderValue] = useState<number>(15);
  const [isInvested, setIsInvested] = useState<boolean>(false);
  const [isInvesting, setIsInvesting] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(TUTORIAL_ROUND_DURATION);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [results, setResults] = useState<TutorialResults | null>(null);
  const [serverStartTime, setServerStartTime] = useState<number | null>(null);
  const [tutorialEndTime, setTutorialEndTime] = useState<number | null>(null);
  const [popupEndTime, setPopupEndTime] = useState<number | null>(null);
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sliderValueRef = useRef<number>(15);
  const isInvestedRef = useRef<boolean>(false);
  const isInvestingRef = useRef<boolean>(false);
  const fetchingResultsRef = useRef<boolean>(false);
  const failsafeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef<boolean>(false);

  // Keep refs in sync with state
  useEffect(() => {
    sliderValueRef.current = sliderValue;
  }, [sliderValue]);

  useEffect(() => {
    isInvestedRef.current = isInvested;
  }, [isInvested]);

  // Initialize tutorial round and get server time
  useEffect(() => {
    console.log('[TutorialRound] Component mounted, initializing tutorial round...');
    const initTutorial = async () => {
      try {
        console.log('[TutorialRound] Calling /tutorialRound/init endpoint...');
        const res = await fetch(`${API_BASE_URL}/tutorialRound/init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': prolificId,
          },
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('[TutorialRound] Tutorial init response:', data);
          const startTime = data.startTime || Date.now();
          const durationMs = TUTORIAL_ROUND_DURATION * 1000;
          const endTime = startTime + durationMs;
          const popupEnd = endTime + POPUP_DURATION * 1000;
          const gameStart = popupEnd;
          setServerStartTime(startTime);
          setTutorialEndTime(endTime);
          setPopupEndTime(popupEnd);
          setGameStartTime(gameStart);
          setTimeLeft(Math.max(0, Math.ceil((endTime - Date.now()) / 1000)));
          setIsInitialized(true);
          console.log('[TutorialRound] Tutorial round initialized successfully');
        } else {
          console.warn('[TutorialRound] Init failed, using fallback timing');
          const now = Date.now();
          setServerStartTime(now);
          setTutorialEndTime(now + TUTORIAL_ROUND_DURATION * 1000);
          setPopupEndTime(now + (TUTORIAL_ROUND_DURATION + POPUP_DURATION) * 1000);
          setGameStartTime(now + (TUTORIAL_ROUND_DURATION + POPUP_DURATION) * 1000);
          setIsInitialized(true);
        }
      } catch (err) {
        console.error('[TutorialRound] Error initializing tutorial:', err);
        const now = Date.now();
        setServerStartTime(now);
        setTutorialEndTime(now + TUTORIAL_ROUND_DURATION * 1000);
        setPopupEndTime(now + (TUTORIAL_ROUND_DURATION + POPUP_DURATION) * 1000);
        setGameStartTime(now + (TUTORIAL_ROUND_DURATION + POPUP_DURATION) * 1000);
        setIsInitialized(true);
      }
    };
    
    initTutorial();
  }, [prolificId]);

  // Helper to show results from backend - use backend values, calculate missing ones if needed
  const calculateAndShowResults = useCallback((data: any) => {
    // CRITICAL: Use backend-provided values when available, calculate if missing
    // This ensures both players see the same numbers when backend provides them
    if (!data.playerInvestment || !data.partnerInvestment) {
      console.error('[Tutorial] Missing investment data from backend:', data);
      return;
    }

    const playerInvestment = data.playerInvestment;
    const partnerInvestment = data.partnerInvestment;
    const initialTokens = INITIAL_COINS_PER_ROUND;
    
    // Use backend values if available, otherwise calculate (ensures we always show results)
    const playerKept = data.playerKept !== undefined ? data.playerKept : (initialTokens - playerInvestment);
    const partnerKept = data.partnerKept !== undefined ? data.partnerKept : (initialTokens - partnerInvestment);
    const poolTotal = data.poolTotal !== undefined ? data.poolTotal : (playerInvestment + partnerInvestment);
    const poolMultiplied = data.poolMultiplied !== undefined ? data.poolMultiplied : Math.floor(poolTotal * 1.5);
    const poolShare = data.poolShare !== undefined ? data.poolShare : Math.floor(poolMultiplied / 2);
    const playerFluctuation = data.playerFluctuation !== undefined ? data.playerFluctuation : 0;
    const partnerFluctuation = data.partnerFluctuation !== undefined ? data.partnerFluctuation : 0;
    const playerEarnings = data.playerEarnings !== undefined ? data.playerEarnings : (playerKept + poolShare + playerFluctuation);
    const partnerEarnings = data.partnerEarnings !== undefined ? data.partnerEarnings : (partnerKept + poolShare + partnerFluctuation);
    const playerNetGain = data.playerNetGain !== undefined ? data.playerNetGain : (playerEarnings - initialTokens);
    const partnerNetGain = data.partnerNetGain !== undefined ? data.partnerNetGain : (partnerEarnings - initialTokens);
    
    // Log what we're using
    const usingBackend = data.playerEarnings !== undefined && data.partnerEarnings !== undefined;
    console.log(`[Tutorial] ${usingBackend ? 'Using backend values' : 'Calculated locally (backend incomplete)'}:`, {
      playerInvestment, 
      partnerInvestment, 
      playerEarnings, 
      partnerEarnings, 
      playerFluctuation, 
      partnerFluctuation 
    });

    console.log('[Tutorial] Showing results from backend (all values from server):', { 
      playerInvestment, 
      partnerInvestment, 
      playerKept,
      partnerKept,
      poolTotal,
      poolMultiplied,
      poolShare,
      playerFluctuation, 
      partnerFluctuation,
      playerEarnings, 
      partnerEarnings,
      playerNetGain,
      partnerNetGain
    });

    setResults({
      playerInvestment,
      partnerInvestment,
      playerKept,
      partnerKept,
      poolTotal,
      poolMultiplied,
      poolShare,
      playerFluctuation,
      partnerFluctuation,
      playerEarnings,
      partnerEarnings,
      playerNetGain,
      partnerNetGain,
    });
    
    setShowResults(true);
  }, []);

  // Handle investment - just mark as invested, results shown when timer hits 0
  const handleInvest = useCallback(async () => {
    if (isInvestedRef.current || isInvestingRef.current) return;

    isInvestingRef.current = true;
    setIsInvesting(true);
    try {
      const url = `${API_BASE_URL}/tutorialRound/invest`;
      console.log(`[Tutorial] Submitting investment to: ${url}`);
      console.log(`[Tutorial] Investment amount: ${sliderValueRef.current}`);
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': prolificId,
        },
        body: JSON.stringify({ amountInvested: sliderValueRef.current }),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: { message: `HTTP ${res.status}` } }));
        console.error(`[Tutorial] Investment failed: ${res.status}`, errorData);
        throw new Error(`Investment failed: ${res.status} - ${JSON.stringify(errorData)}`);
      }
      
      const data = await res.json();
      console.log(`[Tutorial] Investment successful:`, data);
      setIsInvested(true);
      isInvestedRef.current = true;
    } catch (err) {
      console.error('[Tutorial] Error submitting tutorial investment:', err);
    } finally {
      isInvestingRef.current = false;
      setIsInvesting(false);
    }
  }, [prolificId]);

  // Timer synchronized with server - show results when tutorialEndTime is reached
  useEffect(() => {
    if (!tutorialEndTime || showResults) return;

    const updateTimer = () => {
      // Don't update if we're already fetching or results are shown
      if (fetchingResultsRef.current || showResults) {
        return;
      }
      
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((tutorialEndTime - now) / 1000));
      setTimeLeft(remaining);

      // When tutorialEndTime is reached, show results (only once)
      if (now >= tutorialEndTime && !fetchingResultsRef.current && !showResults) {
        fetchingResultsRef.current = true;
        
        // Submit investment if not already done
        if (!isInvestedRef.current) {
          setIsInvested(true);
          isInvestedRef.current = true;
          fetch(`${API_BASE_URL}/tutorialRound/invest`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'prolific-id': prolificId,
            },
            body: JSON.stringify({ amountInvested: sliderValueRef.current }),
          }).catch(err => console.error('Error submitting investment:', err));
        }
        
        // Stop the timer updates
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        // Fetch actual results from backend (with synchronized timing)
        // Retry until backend is ready with both players' investments
        const fetchResults = async (retryCount = 0) => {
          try {
            const url = `${API_BASE_URL}/tutorialRound/results`;
            console.log(`[Tutorial] Fetching results, attempt ${retryCount + 1} from: ${url}`);
            
            const res = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'prolific-id': prolificId,
              },
            });
            
            if (!res.ok) {
              const errorData = await res.json().catch(() => ({ error: { message: `HTTP ${res.status}` } }));
              console.error(`[Tutorial] Results fetch failed: ${res.status}`, errorData);
              throw new Error(`HTTP ${res.status}: ${JSON.stringify(errorData)}`);
            }
            
            const data = await res.json();
            console.log('[Tutorial] Results response:', data);
            
            if (data.ready) {
              // Clear failsafe timer since we got results
              if (failsafeTimerRef.current) {
                clearTimeout(failsafeTimerRef.current);
                failsafeTimerRef.current = null;
              }
              // Update timing from backend response if available
              if (data.popupEndTime) setPopupEndTime(data.popupEndTime);
              if (data.gameStartTime) setGameStartTime(data.gameStartTime);
              // Use ALL backend-provided values to ensure both players see the same numbers
              calculateAndShowResults(data);
              fetchingResultsRef.current = false; // Reset for potential retry
            } else {
              // Not ready yet - retry after a short delay (max 15 retries = 15 seconds)
              if (retryCount < 15) {
                console.log(`[Tutorial] Results not ready yet, retrying in 1s... (${retryCount + 1}/15)`);
                setTimeout(() => fetchResults(retryCount + 1), 1000);
              } else {
                console.error('[Tutorial] Backend not ready after 15 retries - using fallback with simulated data');
                // Previous two-player fallback: simulated partner investment of 15
                // const partnerInvestment = 15; // Default auto-invest value for matched partner
                const playerInvestment = sliderValueRef.current;
                const partnerInvestment = Math.floor(Math.random() * 31); // Random other player fallback (0–30)
                
                // Calculate fallback results
                const fallbackData = {
                  playerInvestment: playerInvestment,
                  partnerInvestment: partnerInvestment,
                  playerKept: 30 - playerInvestment,
                  partnerKept: 30 - partnerInvestment,
                  poolTotal: playerInvestment + partnerInvestment,
                  poolMultiplied: Math.floor((playerInvestment + partnerInvestment) * 1.5),
                  poolShare: Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                  playerFluctuation: 0,
                  partnerFluctuation: 0,
                  playerEarnings: (30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                  partnerEarnings: (30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                  playerNetGain: ((30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30,
                  partnerNetGain: ((30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30
                };
                
                console.log('[Tutorial] Using fallback data:', fallbackData);
                // Clear failsafe timer
                if (failsafeTimerRef.current) {
                  clearTimeout(failsafeTimerRef.current);
                  failsafeTimerRef.current = null;
                }
                calculateAndShowResults(fallbackData);
                fetchingResultsRef.current = false;
              }
            }
          } catch (err) {
            console.error('Error fetching results:', err);
            // Retry on error (max 10 retries)
            if (retryCount < 10) {
              setTimeout(() => fetchResults(retryCount + 1), 1000);
            } else {
              console.error('[Tutorial] Failed to fetch results after retries - using fallback');
              // Use fallback after max retries
              // Previous two-player fallback: const partnerInvestment = 15;
              const playerInvestment = sliderValueRef.current;
              const partnerInvestment = Math.floor(Math.random() * 31);
              const fallbackData = {
                playerInvestment: playerInvestment,
                partnerInvestment: partnerInvestment,
                playerKept: 30 - playerInvestment,
                partnerKept: 30 - partnerInvestment,
                poolTotal: playerInvestment + partnerInvestment,
                poolMultiplied: Math.floor((playerInvestment + partnerInvestment) * 1.5),
                poolShare: Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                playerFluctuation: 0,
                partnerFluctuation: 0,
                playerEarnings: (30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                partnerEarnings: (30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
                playerNetGain: ((30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30,
                partnerNetGain: ((30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30
              };
              // Clear failsafe timer
              if (failsafeTimerRef.current) {
                clearTimeout(failsafeTimerRef.current);
                failsafeTimerRef.current = null;
              }
              calculateAndShowResults(fallbackData);
              fetchingResultsRef.current = false;
            }
          }
        };
        
        fetchResults();
        
        // FAILSAFE: If results aren't shown within 10 seconds, force show fallback
        failsafeTimerRef.current = setTimeout(() => {
          console.warn('[Tutorial] FAILSAFE: Forcing results display after 10s timeout');
          fetchingResultsRef.current = false;
          const playerInvestment = sliderValueRef.current;
          // Previous two-player fallback: const partnerInvestment = 15;
          const partnerInvestment = Math.floor(Math.random() * 31);
          const fallbackData = {
            playerInvestment: playerInvestment,
            partnerInvestment: partnerInvestment,
            playerKept: 30 - playerInvestment,
            partnerKept: 30 - partnerInvestment,
            poolTotal: playerInvestment + partnerInvestment,
            poolMultiplied: Math.floor((playerInvestment + partnerInvestment) * 1.5),
            poolShare: Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
            playerFluctuation: 0,
            partnerFluctuation: 0,
            playerEarnings: (30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
            partnerEarnings: (30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2),
            playerNetGain: ((30 - playerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30,
            partnerNetGain: ((30 - partnerInvestment) + Math.floor(Math.floor((playerInvestment + partnerInvestment) * 1.5) / 2)) - 30
          };
          calculateAndShowResults(fallbackData);
        }, 10000); // 10 second failsafe
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [tutorialEndTime, showResults, prolificId, calculateAndShowResults]);

  // Clear failsafe timer when results are shown
  useEffect(() => {
    if (showResults && failsafeTimerRef.current) {
      clearTimeout(failsafeTimerRef.current);
      failsafeTimerRef.current = null;
    }
  }, [showResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (failsafeTimerRef.current) clearTimeout(failsafeTimerRef.current);
    };
  }, []);

  // Popup countdown state
  const [popupTimeLeft, setPopupTimeLeft] = useState<number>(POPUP_DURATION);
  const popupTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-close popup at synchronized popupEndTime
  useEffect(() => {
    if (!showResults || !popupEndTime) return;
    
    let gameInitialized = false;
    
    const updatePopupTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((popupEndTime - now) / 1000));
      setPopupTimeLeft(remaining);
      
      // When popupEndTime is reached, transition to waiting room (game init happens after partner match)
      if (now >= popupEndTime && !gameInitialized && !hasCompletedRef.current) {
        gameInitialized = true;
        hasCompletedRef.current = true;
        console.log('[TutorialRound] Popup timer expired, transitioning after practice round...');

        // Previous flow: initialized first game round here after practice (when practice was after partner match)
        // fetch(`${API_BASE_URL}/tutorialRound/initGameRound`, {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //     'prolific-id': prolificId,
        //   },
        // })
        // .then(res => res.json())
        // .then(data => {
        //   console.log('[TutorialRound] Game round initialized:', data);
        //   onComplete();
        // })
        // .catch(err => {
        //   console.error('[TutorialRound] Error initializing game round:', err);
        //   onComplete();
        // });

        onComplete();
      }
    };
    
    updatePopupTimer();
    popupTimerRef.current = setInterval(updatePopupTimer, 100);
    
    return () => {
      if (popupTimerRef.current) clearInterval(popupTimerRef.current);
    };
  }, [showResults, popupEndTime, onComplete, prolificId]);

  // Show loading state if not initialized yet
  if (!isInitialized) {
    return (
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          textAlign: 'center',
          padding: '15px 20px',
          marginBottom: '20px',
          borderRadius: '12px',
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          border: '2px solid #4caf50',
          fontWeight: '600',
        }}>
          PRACTICE ROUND
        </div>
        <p>Initializing practice round...</p>
      </div>
    );
  }

  // Render results popup - Simplified flow visualization
  if (showResults && results) {
    return (
      <div className="tutorial-results-overlay">
        <div className="tutorial-results-container" style={{ maxWidth: '700px' }}>
          <div className="tutorial-results-header">
            <h2>Practice Round Results</h2>
          </div>

          <div style={{ padding: '24px 32px' }}>
            {/* Step 1: Investments */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', marginBottom: '8px' }}>
                <div style={{ 
                  width: '32px', height: '32px', borderRadius: '50%', 
                  background: '#3b82f6', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '16px', marginRight: '12px'
                }}>1</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Both players invest from {INITIAL_COINS_PER_ROUND} coins</div>
              </div>
              <div style={{ display: 'flex', gap: '24px', fontSize: '18px', fontWeight: 600, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>You:{' '}<span style={{ color: '#3b82f6' }}>{results.playerInvestment}</span></span>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <img src={randomOpponentAvatar} alt="" className="practice-results-random-icon" />
                  <span>
                    Random Computer Player:{' '}
                    <span style={{ color: '#dc2626' }}>{results.partnerInvestment}</span>
                  </span>
                </span>
              </div>
            </div>

            {/* Arrow */}
            <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: '20px', margin: '8px 0' }}>↓</div>

            {/* Step 2: Pool */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', marginBottom: '8px' }}>
                <div style={{ 
                  width: '32px', height: '32px', borderRadius: '50%', 
                  background: '#10b981', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '16px', marginRight: '12px'
                }}>2</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Investments pooled and multiplied by 1.5×</div>
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, textAlign: 'center' }}>
                ({results.playerInvestment} + {results.partnerInvestment}) × 1.5 = <span style={{ color: '#10b981' }}>{results.poolMultiplied}</span> → Each gets <span style={{ color: '#10b981' }}>{results.poolShare}</span>
              </div>
            </div>

            {/* Arrow */}
            <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: '20px', margin: '8px 0' }}>↓</div>

            {/* Step 3: Final Results */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', marginBottom: '8px' }}>
                <div style={{ 
                  width: '32px', height: '32px', borderRadius: '50%', 
                  background: '#f59e0b', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '16px', marginRight: '12px'
                }}>3</div>
                <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>Final reward = Round earnings<br />(Not invested coins + Pool Share + Market Fluctuation <span style={{ color: '#9ca3af', fontSize: '12px' }}>±3</span>)</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', width: '100%', maxWidth: '500px' }}>
                {/* You */}
                <div style={{ 
                  background: '#eff6ff', 
                  border: '2px solid #3b82f6', 
                  borderRadius: '12px', 
                  padding: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: 600, marginBottom: '8px' }}>You</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {results.playerKept} + {results.poolShare} <span style={{ color: results.playerFluctuation >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{results.playerFluctuation >= 0 ? '+' : ''}{results.playerFluctuation}</span>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e40af' }}>
                    {results.playerEarnings} <img src={coin} alt="coin" style={{ height: '20px', verticalAlign: 'middle' }} />
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600,
                    color: results.playerNetGain >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {results.playerNetGain >= 0 ? '+' : ''}{results.playerNetGain} net
                  </div>
                </div>
                {/* Partner */}
                <div style={{ 
                  background: '#fff1f2', 
                  border: '2px solid #f87171', 
                  borderRadius: '12px', 
                  padding: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={randomOpponentAvatar} alt="" className="practice-results-random-icon" />
                    Random Computer Player
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {results.partnerKept} + {results.poolShare} <span style={{ color: results.partnerFluctuation >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{results.partnerFluctuation >= 0 ? '+' : ''}{results.partnerFluctuation}</span>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#5b21b6' }}>
                    {results.partnerEarnings} <img src={coin} alt="coin" style={{ height: '20px', verticalAlign: 'middle' }} />
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600,
                    color: results.partnerNetGain >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {results.partnerNetGain >= 0 ? '+' : ''}{results.partnerNetGain} net
                  </div>
                </div>
              </div>
            </div>

            {/* Timer text */}
            <div style={{
              width: 'calc(100% + 64px)',
              marginLeft: '-32px',
              marginRight: '-32px',
              marginBottom: '-24px',
              padding: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '0 0 24px 24px',
              fontSize: '18px',
              fontWeight: 600,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span>Continuing to partner matching in <strong style={{ fontSize: '22px' }}>{popupTimeLeft}</strong> seconds</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showInvestReminder =
    !isInvested && !isInvesting && timeLeft > 0 && timeLeft <= 10;

  // Render the same UI as GamePlay
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header banner */}
      <div style={{
        textAlign: 'center',
        padding: '15px 20px',
        marginBottom: '20px',
        borderRadius: '12px',
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        border: '2px solid #4caf50',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          position: 'relative',
          marginBottom: '8px'
        }}>
          <span style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '4px 16px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '700',
            marginBottom: '8px'
          }}>PRACTICE ROUND</span>
        </div>
        Practice Round: {timeLeft} seconds remaining
      </div>

      <div className="practice-round-layout">
        {/* Game Play Panel - Same as real game */}
        <div className="game-play">
          <div className="gameplay-panel-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            Practice Round
          </div>
          
          <div className="game-content">
            <div className="stats-container new-stats-layout stats-container--partner-timer">
              <div className="stat-box partner-round-earning-box practice-random-stat-box">
                <img src={randomOpponentAvatar} alt="" className="practice-random-stat-icon" />
                <div className="practice-random-stat-label">Random</div>
                <div className="practice-random-stat-sublabel">Computer player</div>
              </div>
              <div className="stat-box timer-box">
                <div className="stat-value" style={{ color: timeLeft <= 5 ? '#dc2626' : undefined }}>{timeLeft}</div>
                <div className="stat-label">Time Left</div>
              </div>
              {/* <div className="stat-box your-total-earning-box">
                <div className="stat-value">
                  0 <span className="coin-icon"><img src={coin} alt="coin" style={{height: '1em', verticalAlign: 'middle'}} /></span>
                </div>
                <div className="stat-label">Total Earning</div>
              </div> */}
            </div>

            <div className="investment-container">
              <div className="investment-header">
                <span>Round Investment</span>
                <div className="investment-value">
                  <input
                    type="text"
                    className="investment-input"
                    value={sliderValue}
                    readOnly
                  />
                  <span className="coin-icon"><img src={coin} alt="coin" style={{height: '1em', verticalAlign: 'middle'}} /></span>
                </div>
              </div>

              <div className="slider-container" style={{ padding: '0 10px' }}>
                <div
                  className="slider-filled"
                  style={{
                    width: `calc(${(sliderValue / 30) * 100}% + 0.5px)`,
                  }}
                ></div>
                <input
                  type="range"
                  min={0}
                  max={30}
                  step={1}
                  value={sliderValue}
                  onChange={(e) => setSliderValue(Number(e.target.value))}
                  className="investment-slider"
                  disabled={isInvested || isInvesting}
                  style={{
                    width: 'calc(100% + 20px)',
                    marginLeft: '-10px',
                    marginRight: '-10px',
                  }}
                />
                <div className="slider-labels">
                  <div
                    className="slider-ticks"
                    style={{
                      position: 'relative',
                      width: '100%',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {[...Array(31)].map((_, idx) => {
                      const left = (idx / 30) * 100;
                      return (
                        <div
                          className="tick-mark"
                          key={idx}
                          style={{
                            position: 'absolute',
                            left: `calc(${left}% - 1px)`,
                            alignItems: 'center',
                            width: '0',
                          }}
                        >
                          <div
                            className="tick-line"
                            style={{
                              height: idx % 5 === 0 ? 10 : 6,
                              backgroundColor: idx % 5 === 0 ? '#6B7280' : '#9CA3AF',
                            }}
                          ></div>
                          {idx % 2 === 0 && (
                            <div className="tick-value" style={{ marginTop: 4, fontSize: 10 }}>
                              {idx}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="slider-descriptions">
                    <span className="slider-desc-left">Less at stake, less to gain</span>
                    <span className="slider-desc-right">More at stake, more to gain</span>
                  </div>
                </div>
              </div>

              <div className="invest-button-wrap">
                {showInvestReminder && (
                  <div className="invest-button-reminder" role="alert">
                    Click the Invest button to save your investment!
                  </div>
                )}
                <button
                  className={`invest-button${isInvested ? ' invested' : ''}`}
                  onClick={handleInvest}
                  disabled={isInvested || isInvesting}
                >
                  {isInvested ? '✓ Invested!' : isInvesting ? 'Investing...' : 'Invest'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Panel — How it works */}
        <div className="practice-how-panel">
          <div className="practice-how-header">
            <div className="practice-how-header-icon" aria-hidden="true">💡</div>
            <h3>How it works</h3>
          </div>

          <div className="practice-how-steps-box">
            <div className="practice-how-step">
              <span className="practice-how-step-num" aria-hidden="true">1</span>
              <div className="practice-how-step-body">
                <p className="practice-how-step-text">
                  Both players start with <strong>{INITIAL_COINS_PER_ROUND} coins</strong>.
                </p>
              </div>
            </div>

            <div className="practice-how-step">
              <span className="practice-how-step-num" aria-hidden="true">2</span>
              <div className="practice-how-step-body">
                <p className="practice-how-step-text">
                  Choose how many coins to invest in the <strong>shared pool</strong>.
                </p>
              </div>
            </div>

            <div className="practice-how-step">
              <span className="practice-how-step-num" aria-hidden="true">3</span>
              <div className="practice-how-step-body">
                <p className="practice-how-step-text">
                  The pool grows by <strong>50% (×1.5)</strong> and is split equally.
                </p>
              </div>
            </div>

            <div className="practice-how-step practice-how-step--last">
              <span className="practice-how-step-num" aria-hidden="true">4</span>
              <div className="practice-how-step-body">
                <p className="practice-how-step-text">
                  <strong>Final coins =</strong> coins kept + pool share + market fluctuation{' '}
                  <span className="practice-how-formula-range">(−3 to +3)</span>
                </p>
              </div>
            </div>
          </div>

          <div className="practice-how-example">
            <strong>Example:</strong> You invest 20, the other player invests 10.
            <div>Pool = 30 × 1.5 = 45 → Each gets 22.</div>
            <div>You: 10 + 22 + 2 = <strong>34</strong> coins.</div>
            <div>Other player: 20 + 22 − 1 = <strong>41</strong> coins.</div>
          </div>
          <div className="practice-how-bonus">
            🏆 <strong>Bonus:</strong> Only the one real investment round (after matching) counts toward your study bonus. This practice round does not.
          </div>
        </div>
      </div>

    </div>
  );
};

export default TutorialRound;
